import { AddCommentOutlined, HistoryToggleOffOutlined, PushPinOutlined } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { AppDispatch } from 'redux/store';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { myFolder } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import { selectFolders, setCurrentFolder } from '../../redux/reducers/folderReducer';
import { StyledDrawerButton } from '../../theme/CustomComponents';
import { AppEnum } from '../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useFeatures } from '../Providers/FeatureProvider';

const DrawerPrimaryButtonsContainer = styled('div')<{ drawerOpen: boolean }>(({ drawerOpen }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderBottom: '1px solid #C4C4C4',
  padding: '10px 0px',
  alignItems: drawerOpen ? 'stretch' : 'center',
}));

const DrawerPrimaryButtons = ({
  showMyFolderButton,
}: {
  showMyFolderButton: boolean;
}): JSX.Element => {
  const analytics = useAnalytics();
  const navigate = useClassicNavigate();
  const features = useFeatures();
  const location = useLocation();
  const { folderId } = useParams<{ folderId?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedApp, drawerOpen } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);

  const handleNewTemporaryChat = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_NEW_PRIVATE_CHAT);
    navigate('/temporary-chat?clear=true');
    dispatch(setCurrentFolder(undefined));
  }, [analytics, navigate, dispatch]);

  const handleNewChat = useCallback(() => {
    if (currentFolder?.id !== myFolder.id) {
      analytics.event(AnalyticsEvent.WM_NIGEL_DEFAULT_FOLDER);
      dispatch(setCurrentFolder(myFolder));
    }
    navigate(`/folder/${myFolder.id}`);
  }, [analytics, currentFolder?.id, dispatch, navigate]);

  const handlePinnedChats = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_PINNED_CHATS);
    navigate('/pinned-chats');
  }, [analytics, navigate]);

  // Expanded view - show full buttons with text
  return (
    <DrawerPrimaryButtonsContainer drawerOpen={drawerOpen} id="drawer-primary-buttons">
      {showMyFolderButton && (
        <StyledDrawerButton
          variant={drawerOpen ? 'contained' : 'outlined'}
          isActive={folderId === myFolder.id}
          onClick={handleNewChat}
          sx={{
            ...(drawerOpen && {
              borderRadius: '8px',
              bgcolor: '#070154',
              '&:hover': {
                color: '#070154',
                '& h6': {
                  color: '#070154',
                },
              },
            }),
          }}
        >
          <AddCommentOutlined sx={{ height: '20px', width: '20px' }} />
          {drawerOpen && (
            <Typography
              variant="h6"
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                color: 'white',
              }}
            >
              New Chat
            </Typography>
          )}
        </StyledDrawerButton>
      )}
      {selectedApp !== AppEnum.DEAL_GPT && features.private_chat && (
        <StyledDrawerButton
          variant="outlined"
          onClick={handleNewTemporaryChat}
          isActive={location.pathname === '/temporary-chat'}
        >
          <HistoryToggleOffOutlined sx={{ height: '20px', width: '20px' }} />
          {drawerOpen && (
            <Typography
              variant="h6"
              sx={{
                fontSize: '16px',
                fontWeight: '700',
              }}
            >
              Temporary Chat
            </Typography>
          )}
        </StyledDrawerButton>
      )}
      <StyledDrawerButton
        variant="outlined"
        onClick={handlePinnedChats}
        isActive={location.pathname === '/pinned-chats'}
      >
        <PushPinOutlined sx={{ color: '#070154', height: '20px', width: '20px' }} />
        {drawerOpen && (
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: '700',
            }}
          >
            Pinned Chats
          </Typography>
        )}
      </StyledDrawerButton>
    </DrawerPrimaryButtonsContainer>
  );
};

export default DrawerPrimaryButtons;
